/**
 * ErrorBanner: the inline four answers (what happened, is my work safe,
 * what do I do, who fixes it) for a read or a save that failed anywhere
 * outside the AI tools' own registries. The copy comes from
 * errorCopy.ts (page_read / page_write); `what` names the thing on this
 * screen; the reference the server put on the failure is shown so the
 * person can quote it. Try again appears only where trying again can
 * work; a lapsed session gets a Sign in door instead; nothing raw ever
 * reaches the screen.
 */
import type { ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { ApiError } from '@/lib/api';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  pageReadKeyOf,
  pageReadText,
  pageWriteKeyOf,
  pageWriteText,
  referenceOf,
  shortRef,
  SESSION_EXPIRED_KEY,
  SESSION_EXPIRED_RETRY_KEY,
} from '@/lib/errorCopy';

export interface ErrorBannerProps {
  error: unknown;
  onRetry?: () => void;
  /** The thing that failed to load, as the screen calls it: "the notes",
   *  "your tasks". Reads only; a save's copy needs no noun. */
  what?: string;
  /** A failed read (the default) or a failed save. */
  kind?: 'read' | 'write';
  'data-testid'?: string;
}

const BANNER_CLS = 'my-3 rounded-[12px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700';

/** The keys where one more go can work. */
const RETRIABLE = new Set(['server', 'busy', 'offline', 'unreachable']);

function signInHref(): string {
  const ret = window.location.pathname + window.location.search + window.location.hash;
  return `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(ret)}`;
}

export function ErrorBanner({
  error,
  onRetry,
  what = 'this part of the page',
  kind = 'read',
  'data-testid': testId,
}: ErrorBannerProps): ReactNode {
  const contact = useSupportContact();
  const key = kind === 'write' ? pageWriteKeyOf(error) : pageReadKeyOf(error);
  // A lapsed session, or a sign-in that ran out mid-work: either way the
  // door back is Sign in.
  const signIn = key === 'session' || key === SESSION_EXPIRED_KEY || key === SESSION_EXPIRED_RETRY_KEY;
  const ref = referenceOf(error);
  const text = kind === 'write' ? pageWriteText(error, contact) : pageReadText(error, contact, what);
  // A 4xx answer does not heal by retrying, whatever its key.
  const clientError = error instanceof ApiError && error.status >= 400 && error.status < 500;
  const canRetry = onRetry !== undefined && !clientError && (key === null || RETRIABLE.has(key));
  return (
    <div role="alert" className={BANNER_CLS} data-testid={testId}>
      <div>{text}</div>
      {(canRetry || signIn || ref !== null) && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {canRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
          {signIn && (
            <a
              href={signInHref()}
              className="inline-flex min-h-9 items-center rounded-pill bg-sage-800 px-4 text-sm font-semibold text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              Sign in
            </a>
          )}
          {ref !== null && (
            <span className="font-mono text-xs text-ink-soft" data-testid="error-ref">
              ref {shortRef(ref)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
